import { useMemo } from 'react'
import { Pmp_Param_Types_Enum, type PmpConfigDetailsFragment, type TokenDetailsFragment } from '@/gql/generated/graphql'
import { hasConditionOverride, getConditionQuery, getConditionParams } from '../_pmp-conditions'

type OverrideContext = {
  token: TokenDetailsFragment
  userAddress?: string
  customizationParams?: Record<string, string>
}

type UsePmpConfigWithOverrideResult = {
  config: PmpConfigDetailsFragment
  isLoading: boolean
  error?: any
  conditionData?: any
  supplementalParams?: Record<string, string>
}

export function usePmpConfigWithOverride(
  originalConfig: PmpConfigDetailsFragment,
  context: OverrideContext
): UsePmpConfigWithOverrideResult {
  // Check if this config has an override
  const hasOverride = hasConditionOverride(originalConfig.key)

  // Get condition parameters using the generic parameter factory
  const conditionParams = useMemo(() => {
    if (!hasOverride) return null
    return getConditionParams(originalConfig.key, context.token)
  }, [hasOverride, originalConfig.key, context.token])

  // Use condition hook if override exists
  const conditionHook = hasOverride ? getConditionQuery(originalConfig.key) : null

  // Pass current customization value for any config with an override
  const selectedValue = useMemo(() => {
    return hasOverride ? context.customizationParams?.[originalConfig.key] : undefined
  }, [hasOverride, originalConfig.key, context.customizationParams])

  const conditionResult = conditionHook ? conditionHook(true, conditionParams, selectedValue) : null

  // Generate final config with override
  const result = useMemo((): UsePmpConfigWithOverrideResult => {
    if (!hasOverride || !conditionResult) {
      return {
        config: originalConfig,
        isLoading: false
      }
    }

    const { data, fetching, error } = conditionResult

    if (fetching) {
      return {
        config: originalConfig,
        isLoading: true,
        conditionData: data
      }
    }

    if (error) {
      return {
        config: originalConfig,
        isLoading: false,
        error,
        conditionData: data
      }
    }

    // Apply override based on condition
    const overriddenConfig = applyConditionOverride(originalConfig, originalConfig.key, data)

    return {
      config: overriddenConfig,
      isLoading: false,
      conditionData: data,
      supplementalParams: conditionResult.supplementalParams
    }
  }, [hasOverride, conditionResult, originalConfig])

  return result
}

function applyConditionOverride(
  originalConfig: PmpConfigDetailsFragment,
  conditionKey: string,
  data: any
): PmpConfigDetailsFragment {
  switch (conditionKey) {
    case 'Splash Screen Squiggle (ROM)':
      if (!Array.isArray(data) || data.length === 0) {
        return originalConfig
      }

      return {
        ...originalConfig,
        param_type: Pmp_Param_Types_Enum.Select,
        select_options: data.map((option: any) => option.value)
      }
    default:
      return originalConfig
  }
}
